import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { YearlyTaxReport } from 'src/components/assets/tables/YearlyTaxReport';
import { AddEditTaxReportModal } from 'src/components/assets/yearlyTaxReport/AddEditTaxReportModal';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useAssetYearlyTaxReports } from 'src/hooks/useAssetYearlyTaxReports';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { IAssetYearlyTaxReports } from 'src/models/index';
import { Routes } from 'src/router/Routes';
import { isNullUndefinedOrEmpty } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Button, Card, CardContent, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAsset } from '../../context-providers/AssetContext';

export const AssetYearlyTaxSite = () => {
  const { t } = useTranslation();
  const { assetId } = useParams<{ assetId: string }>();
  const { asset } = useAsset();
  const [isAddEditTaxReportModalOpen, setIsAddEditTaxReportModalOpen] = useState<boolean>(false);
  const [selectedTaxReport, setSelectedTaxReport] = useState<IAssetYearlyTaxReports | null>();
  const [reload, setReload] = useState<boolean>(false);
  const TaxReportData = useAssetYearlyTaxReports(assetId ?? '', reload);
  const handleCloseModal = () => {
    setIsAddEditTaxReportModalOpen(false);
    setSelectedTaxReport(null);
  };

  const handleOpenModal = (taxReportId?: number | undefined) => {
    setIsAddEditTaxReportModalOpen(true);
    // find tax report by id
    if (typeof taxReportId === 'number') {
      setSelectedTaxReport(TaxReportData[taxReportId]);
      console.log(TaxReportData[taxReportId]);
    } else {
      setSelectedTaxReport(null);
    }
  };

  const handleSaveModal = () => {
    setReload(!reload);
    setIsAddEditTaxReportModalOpen(false);
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: (isNullUndefinedOrEmpty(asset?.descriptions?.title) ? asset?.id : asset?.descriptions?.title) ?? '',
          },
          {
            link: Routes.ASSETS.INVESTMENT.replace(':assetId', asset?.id ?? ''),
            label: t('dashboard.breadcrumbs.investment'),
          },
        ]}
        headline={
          asset
            ? isNullUndefinedOrEmpty(asset.descriptions?.title)
              ? asset.id
              : (asset.descriptions?.title as string)
            : t('dashboard.breadcrumbs.addasset')
        }
      >
        {asset && (
          <AssetDetailsLinkList
            assetId={asset.id}
            useSerialNumber={asset.useSerialNumber}
            active={'yearlyTaxReport'}
            showSecondaryItems={asset?.investmentDetails?.stage === TAssetStage.secondary}
            isPrimary={asset?.investmentDetails?.stage === TAssetStage.primary}
            isSecondary={asset?.investmentDetails?.stage === TAssetStage.secondary}
            isPreorder={asset?.investmentDetails?.stage === TAssetStage.preorder}
            securityType={asset?.investmentDetails?.securityType}
          />
        )}
      </SiteHeader>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 0 }}>
            {t('asset.yearlyTaxReport.title')}
          </Typography>
          <Grid container style={{ maxWidth: '500px' }}>
            <Typography variant="body1">{t('asset.yearlyTaxReport.tagline')}</Typography>
          </Grid>
          <Grid container>
            <Grid flexGrow={1}></Grid>
            <Grid container alignItems="center">
              <span>
                <Button variant="contained" onClick={() => handleOpenModal()}>
                  {t('asset.yearlyTaxReport.button.add')}
                </Button>
              </span>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <div style={{ marginTop: '20px' }}></div>
      <YearlyTaxReport editTaxReport={handleOpenModal} yearlyTaxReports={TaxReportData} />

      {assetId && (
        <AddEditTaxReportModal
          open={isAddEditTaxReportModalOpen}
          handleClose={handleCloseModal}
          handleSave={handleSaveModal}
          selectedTaxReport={selectedTaxReport}
          assetId={assetId}
        />
      )}
    </>
  );
};
